<template>
    <div class="flex flex-col gap-2">
        <UInputMenu
            v-model="picker"
            :search="search"
            placeholder="Search countries..."
            option-attribute="name"
            by="code"
            nullable
            size="lg"
            @update:model-value="onPick"
        />
        <div v-if="selectedCountries.length" class="flex flex-col gap-1">
            <div
                v-for="c in selectedCountries"
                :key="c.value"
                class="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5"
                :class="exclude ? 'bg-red-50' : 'bg-gray-50'"
            >
                <span class="truncate text-sm" :class="exclude ? 'text-red-700' : 'text-gray-700'" :title="c.name">{{ c.name }}</span>
                <UButton icon="i-ph-x-bold" size="2xs" color="gray" variant="ghost" :padded="false" class="shrink-0 p-0.5" @click="removeOne(c.value)" />
            </div>
        </div>
        <div class="flex items-center gap-2">
            <UToggle :model-value="exclude" @update:model-value="setExclude($event)" />
            <span class="text-xs text-gray-500">{{ exclude ? 'Exclude selected' : 'Include selected' }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Filter } from '~/types/filter'
import { searchCountries, getCountryName } from '~/utils/countries'

const emit = defineEmits<{ 'update:filter': [Filter] }>()
const props = defineProps<{ filter: Filter }>()

const picker = ref<any>(null)
const values = computed<string[]>(() => props.filter.value?.values ?? [])
const exclude = computed<boolean>(() => props.filter.value?.exclude ?? false)
const selectedCountries = computed(() => values.value.map(v => ({ value: v, name: getCountryName(v) })))

function emitValue(vals: string[], exc: boolean) {
    if (!vals.length) emit('update:filter', { ...props.filter, value: undefined, selected: undefined })
    else emit('update:filter', { ...props.filter, value: { values: vals, exclude: exc }, selected: { values: vals, exclude: exc } })
}

function onPick(c: { code: string; name: string } | null) {
    if (!c) return
    const code = c.code.toLowerCase()
    if (!values.value.includes(code)) emitValue([...values.value, code], exclude.value)
    nextTick(() => { picker.value = null })
}

function removeOne(code: string) {
    emitValue(values.value.filter(v => v !== code), exclude.value)
}

function setExclude(exc: boolean) {
    emitValue(values.value, exc)
}

async function search(q: string) {
    return searchCountries(q ?? '')
        .map(c => ({ code: c.code, name: c.name }))
        .filter(c => !values.value.includes(c.code.toLowerCase()))
}
</script>
